import React, { useRef, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Card, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../../shared/components/Breadcrumbs";
import { ESCALATION_RULE_LOG } from "../../../../shared/constants/Constants";
import ServerSideGrid from "../../../../shared/components/listview/ServerSideGrid";
import { addTableFilter } from "../../../table_filters_redux/Actions";
import { useSelector } from "react-redux";
import { instance } from "../../../../shared/utilites/ApiClient"; 
import CustomSelectFilter from "../../../../shared/components/listview/CustomSelectFilter";

const ESCALATION_RULE_LOG_API = "tickets/escalation-rules/executions/query";
const pageSize = 80;

const EscalationRuleLog = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Redux
  const { tableFilters } = useSelector((state) => state.tableFilter);
  const tableFilterKey = useMemo(() => location.pathname + "/" + ESCALATION_RULE_LOG, [location]);

  // Saved state restore (sort + filter persist across navigation)
  const savedState = tableFilters[tableFilterKey];
  const currentFilterRef = useRef(savedState?.filter ? JSON.parse(savedState.filter) : {});
  const currentSortRef = useRef(savedState?.sort ? JSON.parse(savedState.sort) : []);
  const gridRef = useRef(null);
  const isGridStabilized = useRef(false);

  // Restore external grid filter from saved state
  const [externalGridFilter, setExternalGridFilter] = React.useState(
  savedState
    ? {
        filterModel: JSON.parse(savedState.filter || "{}"),
        sortModel: JSON.parse(savedState.sort || "null"),
        pageSize: savedState.pageSize,
        columnVisibility: savedState.columnVisibility || null,
        columnOrder: savedState.columnOrder || [],
      }
    : {
        // ← preset: show only failures on first load
        filterModel: {
          execution_status: {
            filterType: "text",
            type: "inArray",
            filter: JSON.stringify(["failure"]),
          },
        },
        sortModel: [],
        columnVisibility: null,
        columnOrder: [],
      }
);

  // Column definitions
  const columnDefs = useMemo(() => [
    {
      headerName: "Rule Name",
      field: "rule_name",
      type: "customLink",
      sortable: true,
      flex: 1,
      minWidth: 200,
      functionTo: (data) => navigate(`/escalationRulesForm/${data.escalation_rule_id}`),
    },
    {
      headerName: "Ticket Number",
      field: "ticket_number",
      type: "customLink",
      sortable: true,
      width: 160,
      pinned: "left",
      functionTo: (data) => navigate(`/ticketing/${data.ticket_id}`),
    },    
    {
      headerName: "Execution Mode",
      field: "execution_mode",
      filter: CustomSelectFilter,
      sortable: true,
      width: 180,
      filterParams: {
        options: ["scheduled", "ticket_creation"],
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Execution Status",
      field: "execution_status",
      filter: CustomSelectFilter,
      sortable: true,
      width: 160,
      filterParams: {
        options: ["success", "failure"],
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Notification Status",
      field: "notification_status",
      filter: CustomSelectFilter,
      sortable: true,
      width: 180,
      filterParams: {
        options: ["success", "partial"],
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Error Message",
      field: "error_message",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 230,
      valueFormatter: (params) => params.value ?? "-",
    },
    {
      headerName: "Executed At",
      field: "executed_at",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 180,
    },
  ], []);

  // Save filter/sort to Redux on grid filter change
  const handleGridFilterChanged = useCallback((filterModel) => {
    currentFilterRef.current = filterModel;
    dispatch(addTableFilter(tableFilterKey, {
      filter: JSON.stringify(filterModel),
      sort: JSON.stringify(currentSortRef.current),
      page: 1,
      pageSize,
    }));
  }, [dispatch, tableFilterKey]);

  // Save sort to Redux on grid sort change
  const handleGridSortChanged = useCallback((sortModel) => {
    currentSortRef.current = sortModel;
    dispatch(addTableFilter(tableFilterKey, {
      filter: JSON.stringify(currentFilterRef.current),
      sort: JSON.stringify(sortModel),
      page: 1,
      pageSize,
    }));
  }, [dispatch, tableFilterKey]);

  // Save column state to Redux
  const handleColumnStateChanged = useCallback(() => {
    if (!isGridStabilized.current || !gridRef.current) return;
    const colApi = gridRef.current.getColumnApi();
    if (!colApi) return;

    const colState = colApi.getColumnState();
    const columnVisibility = {};
    colState.forEach((col) => { columnVisibility[col.colId] = !col.hide; });
    const columnOrder = colState.map((col) => ({
      colId: col.colId,
      width: col.width,
      pinned: col.pinned || null,
    }));

    dispatch(addTableFilter(tableFilterKey, {
      filter: JSON.stringify(currentFilterRef.current),
      sort: JSON.stringify(currentSortRef.current),
      page: 1,
      pageSize,
      columnVisibility,
      columnOrder,
    }));
  }, [dispatch, tableFilterKey]);

  // Reset stabilized flag on unmount
  React.useEffect(() => {
    return () => { isGridStabilized.current = false; };
  }, []);


  return (
    <>
      <Row className="detailsPageHeader">
        <Col>
          <Breadcrumbs
            children={[{ href: "/ticket-escalationrule-log/", label: ESCALATION_RULE_LOG }]}
          />
        </Col>
      </Row>

      <Row>
        <Card className="table-card">
          <Card.Body>
            <ServerSideGrid
              ref={gridRef}
              columnDefs={columnDefs}
              apiEndpoint={ESCALATION_RULE_LOG_API}
              apiPayload={{}}
              tableHeight="70vh"
              cacheBlockSize={pageSize}
              showPagination={true}
              paginationPageSize={pageSize}
              externalFilter={externalGridFilter}
              onFilterChanged={handleGridFilterChanged}
              onSortChanged={handleGridSortChanged}
              onColumnStateChanged={handleColumnStateChanged}
              onGridStabilized={() => { isGridStabilized.current = true; }}
              theme="alpine"
            />
          </Card.Body>
        </Card>
      </Row>
    </>
  );
};

export default EscalationRuleLog;